import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  MapPin,
  Clock,
  ChevronLeft,
  ExternalLink,
  Heart,
  Users,
  Sparkles,
  PawPrint,
} from "lucide-react";
import PublicNav from "@/components/PublicNav";
import { useLocalize } from "@/lib/localize";
import { useT } from "@/lib/translations";
import { useLanguage } from "@/context/LanguageContext";

const formatDate = (iso, locale) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(locale === "en" ? "en-US" : "tr-TR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
};

const photoSrc = (url) =>
  url?.startsWith("http") ? url : `${API.replace(/\/api$/, "")}${url}`;

export default function EventDetailPage() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const Lo = useLocalize();
  const L = useT();
  const { locale } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [brokenPhotos, setBrokenPhotos] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/public/adoption-events/${eventId}`)
      .then((r) => setEvent(r.data))
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
  }, [eventId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background" data-testid="event-detail-loading">
        <PublicNav activeKey="etkinlikler" />
        <div className="flex items-center justify-center py-24">
          <p className="text-muted-foreground">{L("Yükleniyor…")}</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-background" data-testid="event-detail-notfound">
        <PublicNav activeKey="etkinlikler" />
        <div className="flex flex-col items-center justify-center gap-4 py-24 px-4">
          <p className="text-muted-foreground">{L("Etkinlik bulunamadı.")}</p>
          <Link to="/etkinlikler">
            <Button>{L("Etkinliklere Dön")}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isPast = ["completed", "cancelled"].includes(event.status);
  const recap = event.recap || {};
  const photos = (recap.photos || []).filter((p) => !brokenPhotos.includes(p));

  return (
    <div className="min-h-screen bg-background" data-testid="event-detail-page">
      <PublicNav activeKey="etkinlikler" />

      <section className={isPast ? "bg-muted/30" : "bg-gradient-to-br from-amber-50 via-rose-50 to-background"}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Badge className={isPast ? "bg-emerald-100 text-emerald-800" : "bg-primary text-primary-foreground"}>
              {isPast ? L("Geçmiş Etkinlik") : event.status === "active" ? L("Bugün!") : L("Yaklaşan")}
            </Badge>
            {Lo(event, "subtitle") && (
              <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                <Sparkles className="w-3 h-3 inline mr-1" />
                {Lo(event, "subtitle")}
              </span>
            )}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-6xl tracking-tight mb-6">
            {Lo(event, "title")}
          </h1>

          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
            <div className="flex items-start gap-3">
              <Calendar className="w-6 h-6 text-primary mt-1 flex-shrink-0" strokeWidth={1.5} />
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{L("Tarih")}</p>
                <p className="font-medium">{formatDate(event.event_date, locale)}</p>
                <p className="text-sm text-muted-foreground inline-flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {event.start_time}–{event.end_time}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-6 h-6 text-primary mt-1 flex-shrink-0" strokeWidth={1.5} />
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{L("Lokasyon")}</p>
                <p className="font-medium">{Lo(event, "location_name")}</p>
                <p className="text-sm text-muted-foreground">{Lo(event, "location_address")}</p>
                {event.map_url && (
                  <a
                    href={event.map_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline inline-flex items-center gap-1 mt-1"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    {L("Google Maps'te aç")}
                  </a>
                )}
              </div>
            </div>
          </div>

          {Lo(event, "description") && (
            <p className="text-lg text-muted-foreground mt-8 max-w-3xl leading-relaxed">
              {Lo(event, "description")}
            </p>
          )}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 space-y-16">
        {/* Recap stats (past) */}
        {isPast &&
          (recap.adoptions_count > 0 ||
            recap.visitors_estimated > 0 ||
            recap.new_applications > 0 ||
            recap.donations_total > 0) && (
            <section>
              <h2 className="font-heading text-2xl mb-6">{L("Etkinlikten")}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {recap.adoptions_count > 0 && (
                  <StatBlock value={recap.adoptions_count} label={L("Yuva bulan can")} icon={Heart} accent="text-rose-600" />
                )}
                {recap.visitors_estimated > 0 && (
                  <StatBlock value={recap.visitors_estimated} label={L("Ziyaretçi")} icon={Users} />
                )}
                {recap.new_applications > 0 && (
                  <StatBlock value={recap.new_applications} label={L("Başvuru")} />
                )}
                {recap.donations_total > 0 && (
                  <StatBlock
                    value={`${Number(recap.donations_total).toLocaleString(locale === "en" ? "en-US" : "tr-TR")} ${L("TL")}`}
                    label={L("Toplanan bağış")}
                  />
                )}
              </div>
              {recap.notes && (
                <p className="mt-6 text-muted-foreground italic leading-relaxed max-w-3xl">
                  "{recap.notes}"
                </p>
              )}
            </section>
          )}

        {/* Featured dogs */}
        {event.featured_dogs && event.featured_dogs.length > 0 && (
          <section>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
              <h2 className="font-heading text-2xl">
                {isPast ? L("Etkinlikteki Canlar") : L("Seni Bekleyen Canlar")}
              </h2>
              <Badge variant="outline">{event.featured_dogs.length} {L("can")}</Badge>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {event.featured_dogs.map((d) => (
                <Link key={d.dog_id} to={`/can/${d.dog_id}`}>
                  <Card className="card-hover overflow-hidden h-full">
                    <div className="aspect-square bg-muted overflow-hidden">
                      <DogThumb src={d.photo_url} alt={d.name} />
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-heading text-lg mb-1">{d.name}</h3>
                      <p className="text-xs text-muted-foreground">
                        {Lo(d, "breed") || L("Karışık")} ·{" "}
                        {d.gender === "male" ? L("Erkek") : L("Dişi")}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Photo gallery (past) */}
        {photos.length > 0 && (
          <section>
            <h2 className="font-heading text-2xl mb-6">{L("Etkinlikten Kareler")}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {photos.map((p, i) => (
                <a
                  key={`${p}-${i}`}
                  href={photoSrc(p)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block aspect-square rounded-xl overflow-hidden bg-muted"
                >
                  <img
                    src={photoSrc(p)}
                    alt={`${Lo(event, "title")} ${i + 1}`}
                    onError={() => setBrokenPhotos((prev) => (prev.includes(p) ? prev : [...prev, p]))}
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                  />
                </a>
              ))}
            </div>
          </section>
        )}

        {/* CTA for upcoming */}
        {!isPast && (
          <section className="bg-secondary text-secondary-foreground rounded-3xl p-8 sm:p-10 md:p-14 text-center">
            <h2 className="font-heading text-3xl md:text-4xl mb-4">{L("Sen de gel — birlikte bir can yuva bulsun")}</h2>
            <p className="opacity-90 mb-6 max-w-2xl mx-auto">
              {L("Önceden tanışmak istersen profiline göz at, etkinlikte yüz yüze tanış. Sahiplenmesen de gel — sevgi ver, fotoğraf çek, hikâyemizi yay.")}
            </p>
            <Link to="/canlar" className="block w-full sm:inline-block sm:w-auto">
              <Button size="xl" className="btn-pill w-full sm:w-auto whitespace-normal leading-snug px-6 sm:px-8 bg-primary text-primary-foreground shadow-lg shadow-primary/20 transition-all duration-200 hover:bg-primary hover:text-white hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/30" data-testid="event-detail-cta-btn">
                {L("Canları Tanımaya Başla")}
              </Button>
            </Link>
          </section>
        )}
      </div>

      <footer className="py-12 bg-card border-t border-border/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center text-sm text-muted-foreground">
          {L("Pativista ile birlikte — KAHU dijital destek altyapısı")}
        </div>
      </footer>
    </div>
  );
}

function DogThumb({ src, alt }) {
  const [broken, setBroken] = useState(false);
  if (!src || broken) {
    return (
      <div
        className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/40"
        data-testid="dog-thumb-fallback"
      >
        <PawPrint className="w-8 h-8 text-muted-foreground/50" strokeWidth={1.5} />
      </div>
    );
  }
  return (
    <img src={src} alt={alt} onError={() => setBroken(true)} className="w-full h-full object-cover" />
  );
}

function StatBlock({ value, label, icon: Icon, accent = "text-primary" }) {
  return (
    <div className="text-center bg-card border border-border/50 rounded-2xl p-5">
      {Icon && <Icon className={`w-6 h-6 mx-auto mb-2 ${accent}`} strokeWidth={1.5} />}
      <p className={`font-heading text-3xl ${accent}`}>{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  );
}
